import { useRequest } from 'ahooks';
import useFilter from './useFilter';
import { getPlatformData } from './service';
import { message, Tooltip } from 'antd';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { initEcharts } from 'lib/echarts';
import { formatNumber, NumeralFormatPattern } from 'utils/numberFormat';
import { getChartMaxValue } from '../Activity/useActivityContents';

function Lazada({
  visible,
  departments,
  onUpdateTimeChange,
}: {
  visible: boolean;
  departments: Dashboard.Department[];
  onUpdateTimeChange: Dispatch<SetStateAction<string | undefined>>;
}) {
  const {
    t,
    storeId,
    range: [start, end],
    filter,
  } = useFilter({
    platform: 'LAZADA',
    visible,
    departments,
  });

  const { loading, data } = useRequest(
    () =>
      getPlatformData({
        storeId: storeId as any,
        startTime: start.format('YYYY-MM-DD HH:mm:ss'),
        endTime: end.format('YYYY-MM-DD HH:mm:ss'),
      }).then((res) => {
        if (res.length > 0) {
          onUpdateTimeChange((pre) => pre || res[0].dt);
        }
        return res
          .map(({ data, ...others }) => {
            return {
              ...others,
              data: (JSON.parse(data) as any[]).reduce<any>((res, cur) => {
                res[cur.type] = cur.score;
                return res;
              }, {}),
            };
          })
          .reverse();
      }),
    {
      ready: Boolean(storeId),
      refreshDeps: [storeId, start, end],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const { positiveSellerRating, responseRate, responseTime } = useMemo(() => {
    if (!data?.length) {
      return {};
    }
    const render = (field: string, formatter: NumeralFormatPattern = '0.00%', percent = 100) => {
      const list = data.map(({ data }) => data[field]).filter((_) => typeof _ === 'number');
      if (!list.length) {
        return undefined;
      }
      return formatNumber(
        list.reduce((res, cur) => res + cur, 0) / list.length / percent,
        formatter,
      );
    };
    return {
      positiveSellerRating: render('POSITIVE_SELLER_RATING'),
      responseRate: render('CHAT_RESPONSE_RATE'),
      responseTime: render('CHAT_RESPONSE_TIME', '0.00', 1),
    };
  }, [data]);

  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data || !chartRef.current) {
      return;
    }

    const chart = initEcharts(chartRef.current);

    const positiveSellerRatingList = data.map(({ data }) => data.POSITIVE_SELLER_RATING);
    const chatResponseRate = data.map(({ data }) => data.CHAT_RESPONSE_RATE);
    const chatResponseTimeList = data.map(({ data }) => data.CHAT_RESPONSE_TIME);
    const max2 = getChartMaxValue(
      chatResponseTimeList.filter((_) => typeof _ === 'number'),
      5,
    );

    chart.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: [t('买家好评率'), t('回复率'), t('平均响应时长')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map(({ countDt }) => countDt),
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: [
        {
          type: 'value',
          min: 0,
          max: 100,
          splitNumber: 5,
          interval: 20,
        },
        {
          type: 'value',
          min: 0,
          max: max2,
          splitNumber: 5,
          interval: max2 / 5,
        },
      ],
      series: [
        {
          name: t('买家好评率'),
          type: 'line',
          data: positiveSellerRatingList,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#5B8FF9',
          },
          tooltip: {
            valueFormatter: (value: any) =>
              typeof value === 'number' ? formatNumber(Number(value) / 100, '0.00%') : '-',
          },
        },
        {
          name: t('回复率'),
          type: 'line',
          data: chatResponseRate,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#F6BD16',
          },
          tooltip: {
            valueFormatter: (value: any) =>
              typeof value === 'number' ? formatNumber(Number(value) / 100, '0.00%') : '-',
          },
        },
        {
          name: t('平均响应时长'),
          type: 'bar',
          yAxisIndex: 1,
          barMaxWidth: '40px',
          data: chatResponseTimeList,
          itemStyle: {
            color: '#5AD8A6',
          },
          tooltip: {
            valueFormatter: (value: any) =>
              typeof value === 'number' ? `${value}${t('marketing.分钟')}` : '-',
          },
        },
      ],
    });

    const handleResize = () => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [data, t]);

  if (!data || !storeId) {
    return (
      <>
        {filter}
        {loading ? <LoadingOutlined /> : <></>}
      </>
    );
  }

  return (
    <>
      {filter}
      <section style={visible ? undefined : { display: 'none' }}>
        <header>
          <h3>{t('客户服务')}</h3>
        </header>
        <div>
          <ul>
            <dl>
              <dt>
                {t('买家好评率')}
                <Tooltip title={t('买家好评率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{positiveSellerRating || '-'}</dd>
            </dl>
            <dl>
              <dt>
                {t('回复率')}
                <Tooltip title={t('回复率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{responseRate || '-'}</dd>
            </dl>
            <dl>
              <dt>
                {t('平均响应时长')}
                <Tooltip title={t('平均响应时长tips2')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{responseTime ? `${responseTime} ${t('marketing.分钟')}` : '-'}</dd>
            </dl>
          </ul>
          <section ref={chartRef} />
        </div>
      </section>
    </>
  );
}

export default Lazada;
